import { useEffect, useMemo, useRef, useState } from 'react';
import { TrendingUp, X } from 'lucide-react';
import { useCreateLead, useLeadPipelines } from '../../hooks/useLeads';
import { useLabels } from '../../hooks/useLabels';
import { usePeople } from '../../hooks/usePeople';
import { useClients } from '../../hooks/useClients';
import { useAuthStore } from '../../store/useAuthStore';
import type {
  CreateLeadInput,
  Lead,
  LeadSource,
  LeadStage,
  LeadTemperature,
} from '../../lib/leadsApi';
import { TEMPERATURE_DOT, TEMPERATURE_OPTIONS, initials, leadSourceText } from './leadConstants';
import { SourceMenu } from './detail/leadInlineEditors';
import { Popover } from '../tasks/linear/Popover';
import { Select } from '../ui/Select';
import { DateField } from '../ui/DateField';

const CURRENCY_OPTIONS = ['RON', 'EUR', 'USD', 'GBP'];

export interface CreateLeadModalProps {
  open: boolean;
  onClose: () => void;
  /** Lock the pipeline (e.g. when opened from a pipeline-scoped board). */
  pipelineId?: string;
  /** Pre-select a stage (e.g. when opened from a Kanban column "+"). */
  stageId?: string;
  /** Called after a lead is successfully created. */
  onCreated?: (lead: Lead) => void;
}

/** First ACTIVE stage (lowest order), or the lowest-order stage as a fallback. */
function firstActiveStage(stages: LeadStage[]): LeadStage | undefined {
  const ordered = [...stages].sort((a, b) => a.order - b.order);
  return ordered.find((s) => s.type === 'ACTIVE') ?? ordered[0];
}

/** Format a numeric string with thousand separators while typing. */
function formatThousands(raw: string): string {
  // Keep digits and a single decimal point.
  const cleaned = raw.replace(/[^\d.]/g, '');
  const firstDot = cleaned.indexOf('.');
  let intPart = firstDot === -1 ? cleaned : cleaned.slice(0, firstDot);
  const decPart = firstDot === -1 ? '' : cleaned.slice(firstDot + 1).replace(/\./g, '');
  intPart = intPart.replace(/^0+(?=\d)/, '');
  const withSep = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return firstDot === -1 ? withSep : `${withSep}.${decPart}`;
}

function parseNumber(formatted: string): number | null {
  const n = Number(formatted.replace(/,/g, ''));
  return Number.isFinite(n) && formatted.trim() !== '' ? n : null;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * CLE-210 — polished Create New Lead modal. Collects all lead fields on first
 * creation with smart defaults (default pipeline, first active stage, Warm,
 * Manual, RON, current user). 2-column layout on desktop, single column on
 * mobile. Reuses the app's `.ui-modal-*`/`.ui-input` design system.
 */
export function CreateLeadModal({
  open,
  onClose,
  pipelineId: lockedPipelineId,
  stageId: presetStageId,
  onCreated,
}: CreateLeadModalProps) {
  const { data: pipelines } = useLeadPipelines();
  const { data: labels } = useLabels('leads');
  const { data: people } = usePeople({ active: 'true' });
  const clientsQuery = useClients({ active: 'true', limit: 100 });
  const currentUser = useAuthStore((s) => s.user);
  const create = useCreateLead();

  const titleRef = useRef<HTMLInputElement>(null);

  // --- Form state ----------------------------------------------------------
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [pipelineId, setPipelineId] = useState('');
  const [stageId, setStageId] = useState('');
  const [temperature, setTemperature] = useState<LeadTemperature>('WARM');
  const [valueInput, setValueInput] = useState('');
  const [currency, setCurrency] = useState('RON');
  const [source, setSource] = useState<LeadSource>('MANUAL');
  const [sourceLabel, setSourceLabel] = useState<string | null>(null);
  const [expectedCloseDate, setExpectedCloseDate] = useState('');
  const [labelIds, setLabelIds] = useState<string[]>([]);
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [clientId, setClientId] = useState('');
  const [assigneeId, setAssigneeId] = useState('');

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const pipelineOptions = useMemo(() => pipelines ?? [], [pipelines]);
  const labelOptions = useMemo(() => labels ?? [], [labels]);
  const assignable = useMemo(() => (people ?? []).filter((p) => p.userId), [people]);
  const clients = useMemo(() => clientsQuery.data?.data ?? [], [clientsQuery.data]);

  const selectedPipeline = pipelineOptions.find((p) => p.id === pipelineId);
  const stageOptions = useMemo(
    () => (selectedPipeline ? [...selectedPipeline.stages].sort((a, b) => a.order - b.order) : []),
    [selectedPipeline],
  );

  // Reset the form to its smart defaults whenever the modal is (re)opened.
  useEffect(() => {
    if (!open) return;
    setTitle('');
    setDescription('');
    setTemperature('WARM');
    setValueInput('');
    setCurrency('RON');
    setSource('MANUAL');
    setSourceLabel(null);
    setExpectedCloseDate('');
    setLabelIds([]);
    setContactName('');
    setContactEmail('');
    setContactPhone('');
    setCompanyName('');
    setClientId('');
    setErrors({});
    setSubmitError(null);
    // Assignee defaults to the current user (matched by Person.userId).
    setAssigneeId(currentUser?.id ?? '');
  }, [open, currentUser?.id]);

  // Smart pipeline default: locked pipeline > default pipeline > first one.
  useEffect(() => {
    if (!open || pipelineOptions.length === 0) return;
    if (pipelineId && pipelineOptions.some((p) => p.id === pipelineId)) return;
    const def =
      (lockedPipelineId && pipelineOptions.find((p) => p.id === lockedPipelineId)) ||
      pipelineOptions.find((p) => p.isDefault) ||
      pipelineOptions[0];
    if (def) setPipelineId(def.id);
  }, [open, pipelineOptions, lockedPipelineId, pipelineId]);

  // Smart stage default: preset (Kanban column) > first ACTIVE stage. Re-resolve
  // when the pipeline changes so the stage always belongs to the pipeline.
  useEffect(() => {
    if (!selectedPipeline) return;
    const belongs = selectedPipeline.stages.some((s) => s.id === stageId);
    if (belongs) return;
    if (presetStageId && selectedPipeline.stages.some((s) => s.id === presetStageId)) {
      setStageId(presetStageId);
      return;
    }
    const first = firstActiveStage(selectedPipeline.stages);
    setStageId(first?.id ?? '');
  }, [selectedPipeline, presetStageId, stageId]);

  // Autofocus the title input on open.
  useEffect(() => {
    if (open) {
      const t = setTimeout(() => titleRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Escape closes the modal.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  function toggleLabel(id: string) {
    setLabelIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  // Auto-fill contact fields from the chosen client.
  function handleClientChange(id: string) {
    setClientId(id);
    if (!id) return;
    const client = clients.find((c) => c.id === id);
    if (!client) return;
    if (client.contactPerson) setContactName(client.contactPerson);
    if (client.email) setContactEmail(client.email);
    if (client.phone) setContactPhone(client.phone);
    if (client.name) setCompanyName(client.name);
  }

  function validate(): boolean {
    const next: Record<string, string> = {};
    const trimmed = title.trim();
    if (!trimmed) next.title = 'Titlul este obligatoriu';
    else if (trimmed.length > 255) next.title = 'Titlul trebuie să aibă cel mult 255 de caractere';
    if (!pipelineId) next.pipelineId = 'Pipeline-ul este obligatoriu';
    if (!stageId) next.stageId = 'Etapa este obligatorie';
    if (contactEmail.trim() && !EMAIL_RE.test(contactEmail.trim()))
      next.contactEmail = 'Introdu o adresă de email validă';
    const parsedValue = parseNumber(valueInput);
    if (valueInput.trim() && (parsedValue === null || parsedValue < 0))
      next.value = 'Valoarea trebuie să fie un număr pozitiv';
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  const isValid =
    title.trim().length > 0 &&
    title.trim().length <= 255 &&
    Boolean(pipelineId) &&
    Boolean(stageId);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    if (create.isPending) return; // double-submit guard
    if (!validate()) return;

    const payload: CreateLeadInput = {
      title: title.trim(),
      description: description.trim() || null,
      pipelineId,
      stageId,
      temperature,
      value: parseNumber(valueInput),
      currency,
      source,
      sourceLabel,
      expectedCloseDate: expectedCloseDate ? new Date(expectedCloseDate).toISOString() : null,
      labelIds: labelIds.length ? labelIds : undefined,
      contactName: contactName.trim() || null,
      contactEmail: contactEmail.trim() || null,
      contactPhone: contactPhone.trim() || null,
      companyName: companyName.trim() || null,
      clientId: clientId || null,
      assigneeId: assigneeId || null,
    };

    create.mutate(payload, {
      onSuccess: (lead) => {
        onCreated?.(lead);
        onClose();
      },
      onError: (err) => {
        setSubmitError(err instanceof Error ? err.message : 'Crearea lead-ului a eșuat');
      },
    });
  }

  // Enter submits unless focus is in a textarea (or on the Cancel button).
  function handleFormKeyDown(e: React.KeyboardEvent) {
    if (e.key !== 'Enter') return;
    const target = e.target as HTMLElement;
    if (target.tagName === 'TEXTAREA') return;
    if (target.tagName === 'BUTTON') return;
    e.preventDefault();
    handleSubmit(e as unknown as React.FormEvent);
  }

  const noPipelines = pipelineOptions.length === 0;
  const onlyOnePipeline = pipelineOptions.length === 1;

  const errText = (key: string) =>
    errors[key] ? <p className="mt-1 text-sm text-red-600">{errors[key]}</p> : null;

  return (
    <div
      className="ui-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-lead-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="ui-modal-panel w-full max-w-[640px] max-h-[90vh] p-0">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <h2
            id="create-lead-title"
            className="flex items-center gap-2 text-lg font-semibold text-gray-900"
          >
            <TrendingUp size={18} className="text-brand-600" />
            Creează lead nou
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
            aria-label="Închide"
          >
            <X size={18} />
          </button>
        </div>

        {noPipelines ? (
          <div className="px-5 py-8 text-center text-sm text-gray-600">
            Configurează mai întâi un pipeline în Setări.{' '}
            <a href="/settings/leads" className="text-brand-600 underline">
              Mergi la Setări
            </a>
          </div>
        ) : (
          <form onSubmit={handleSubmit} onKeyDown={handleFormKeyDown}>
            <div className="max-h-[calc(90vh-9rem)] overflow-y-auto px-5 py-4">
              {/* Title */}
              <label className="block text-sm font-medium text-gray-700">
                Titlu <span className="text-red-500">*</span>
                <input
                  ref={titleRef}
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={255}
                  placeholder="ex.: TechCorp - Redesign website"
                  className="ui-input mt-1 text-base"
                  aria-invalid={Boolean(errors.title)}
                />
              </label>
              {errText('title')}

              {/* Description */}
              <label className="mt-4 block text-sm font-medium text-gray-700">
                Descriere
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  placeholder="Scurtă descriere a oportunității..."
                  className="ui-textarea mt-1 resize-y"
                />
              </label>
              <p className="mt-1 text-xs text-gray-400">Acceptă markdown</p>

              {/* Two columns */}
              <div className="mt-5 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
                {/* ---- Left column: Lead Details ---- */}
                <div className="flex flex-col gap-4">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                    Detalii lead
                  </h3>

                  {/* Pipeline */}
                  {onlyOnePipeline ? (
                    <div className="block text-sm font-medium text-gray-700">
                      Pipeline <span className="text-red-500">*</span>
                      <div className="ui-input mt-1 bg-gray-50 text-gray-600">
                        {selectedPipeline?.name}
                      </div>
                    </div>
                  ) : (
                    <Select
                      label="Pipeline"
                      required
                      value={pipelineId}
                      onChange={setPipelineId}
                      disabled={Boolean(lockedPipelineId)}
                      options={pipelineOptions.map((p) => ({ value: p.id, label: p.name }))}
                    />
                  )}
                  {errText('pipelineId')}

                  {/* Stage */}
                  <Select
                    label="Etapă"
                    required
                    value={stageId}
                    onChange={setStageId}
                    options={stageOptions.map((s) => ({
                      value: s.id,
                      label: s.name,
                      color: s.color,
                    }))}
                  />
                  {errText('stageId')}

                  {/* Temperature */}
                  <div className="block text-sm font-medium text-gray-700">
                    Temperatură
                    <div className="mt-1.5 flex gap-2">
                      {TEMPERATURE_OPTIONS.map((o) => {
                        const active = temperature === o.value;
                        return (
                          <button
                            key={o.value}
                            type="button"
                            onClick={() => setTemperature(o.value)}
                            aria-pressed={active}
                            className={`inline-flex flex-1 items-center justify-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition ${
                              active
                                ? 'border-brand-500 bg-brand-50 font-medium text-gray-900'
                                : 'border-gray-300 text-gray-600 hover:border-gray-400'
                            }`}
                          >
                            <span
                              aria-hidden
                              className={`h-2.5 w-2.5 rounded-full ${TEMPERATURE_DOT[o.value]}`}
                            />
                            {o.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Value + Currency */}
                  <div className="flex gap-2">
                    <label className="flex-1 text-sm font-medium text-gray-700">
                      Valoare
                      <input
                        type="text"
                        inputMode="decimal"
                        value={valueInput}
                        onChange={(e) => setValueInput(formatThousands(e.target.value))}
                        placeholder="0.00"
                        className="ui-input mt-1"
                        aria-invalid={Boolean(errors.value)}
                      />
                    </label>
                    <Select
                      label="Monedă"
                      value={currency}
                      onChange={setCurrency}
                      options={CURRENCY_OPTIONS.map((c) => ({ value: c, label: c }))}
                      className="w-24"
                    />
                  </div>
                  {errText('value')}

                  {/* Source */}
                  <div className="text-sm font-medium text-gray-700">
                    Sursă
                    <Popover
                      matchTriggerWidth
                      trigger={({ toggle, ref }) => (
                        <button
                          ref={ref}
                          type="button"
                          onClick={toggle}
                          className="ui-input mt-1 w-full text-left font-normal"
                        >
                          {leadSourceText({ source, sourceLabel })}
                        </button>
                      )}
                    >
                      {(close) => (
                        <SourceMenu
                          current={{ source, sourceLabel }}
                          onPick={(v) => {
                            setSource(v);
                            setSourceLabel(null);
                            close();
                          }}
                          onCustom={(l) => {
                            setSource('OTHER');
                            setSourceLabel(l);
                            close();
                          }}
                        />
                      )}
                    </Popover>
                  </div>

                  {/* Expected Close Date */}
                  <label className="block text-sm font-medium text-gray-700">
                    Data estimată de închidere
                    <DateField
                      value={expectedCloseDate}
                      onChange={setExpectedCloseDate}
                      className="mt-1 w-full"
                    />
                  </label>

                  {/* Labels */}
                  {labelOptions.length > 0 && (
                    <div className="block text-sm font-medium text-gray-700">
                      Etichete
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {labelOptions.map((l) => {
                          const active = labelIds.includes(l.id);
                          return (
                            <button
                              key={l.id}
                              type="button"
                              onClick={() => toggleLabel(l.id)}
                              aria-pressed={active}
                              className={`rounded-full border px-2.5 py-0.5 text-xs transition ${
                                active
                                  ? 'border-transparent'
                                  : 'border-gray-300 opacity-50 hover:opacity-100'
                              }`}
                              style={
                                active ? { backgroundColor: l.color, color: '#fff' } : undefined
                              }
                            >
                              {l.name}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* ---- Right column: Contact Info + Assignment ---- */}
                <div className="flex flex-col gap-4">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                    Informații de contact
                  </h3>

                  <label className="block text-sm font-medium text-gray-700">
                    Nume contact
                    <input
                      type="text"
                      value={contactName}
                      onChange={(e) => setContactName(e.target.value)}
                      placeholder="Numele persoanei de contact"
                      className="ui-input mt-1"
                    />
                  </label>

                  <label className="block text-sm font-medium text-gray-700">
                    Email
                    <input
                      type="email"
                      value={contactEmail}
                      onChange={(e) => setContactEmail(e.target.value)}
                      placeholder="contact@company.com"
                      className="ui-input mt-1"
                      aria-invalid={Boolean(errors.contactEmail)}
                    />
                  </label>
                  {errText('contactEmail')}

                  <label className="block text-sm font-medium text-gray-700">
                    Telefon
                    <input
                      type="tel"
                      value={contactPhone}
                      onChange={(e) => setContactPhone(e.target.value)}
                      placeholder="+40 7XX XXX XXX"
                      className="ui-input mt-1"
                    />
                  </label>

                  <label className="block text-sm font-medium text-gray-700">
                    Nume companie
                    <input
                      type="text"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      placeholder="Companie / Organizație"
                      className="ui-input mt-1"
                    />
                  </label>

                  <h3 className="mt-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
                    Atribuire
                  </h3>

                  <Select
                    label="Client (opțional)"
                    clearable
                    searchable
                    value={clientId}
                    onChange={handleClientChange}
                    placeholder={clients.length === 0 ? 'Încă niciun client' : 'Fără client'}
                    options={clients.map((c) => ({
                      value: c.id,
                      label: c.name,
                      description: c.contactPerson ?? undefined,
                    }))}
                  />

                  <div className="block text-sm font-medium text-gray-700">
                    <Select
                      label="Responsabil"
                      clearable
                      searchable
                      value={assigneeId}
                      onChange={setAssigneeId}
                      placeholder={
                        assignable.length === 0 ? 'Niciun membru de echipă' : 'Neatribuit'
                      }
                      options={assignable.map((p) => ({
                        value: p.userId as string,
                        label: p.fullName,
                      }))}
                    />
                    {/* Assignee avatar/initials preview. */}
                    {assigneeId && (
                      <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
                        <AssigneeAvatar
                          name={
                            assignable.find((p) => p.userId === assigneeId)?.fullName ??
                            currentUser?.name ??
                            ''
                          }
                          avatarUrl={
                            assignable.find((p) => p.userId === assigneeId)?.avatarUrl ?? null
                          }
                        />
                        <span>
                          {assignable.find((p) => p.userId === assigneeId)?.fullName ??
                            currentUser?.name}
                          {assigneeId === currentUser?.id ? ' (tu)' : ''}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {submitError && <p className="mt-4 text-sm text-red-600">{submitError}</p>}
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-2 border-t border-border px-5 py-4">
              <button type="button" onClick={onClose} className="ui-btn-secondary">
                Anulează
              </button>
              <button
                type="submit"
                disabled={!isValid || create.isPending}
                className="ui-btn-primary"
              >
                {create.isPending ? 'Se creează…' : 'Creează lead'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function AssigneeAvatar({ name, avatarUrl }: { name: string; avatarUrl: string | null }) {
  if (avatarUrl) {
    return <img src={avatarUrl} alt="" className="h-5 w-5 rounded-full object-cover" />;
  }
  return (
    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-brand-100 text-[10px] font-semibold text-brand-700">
      {initials(name)}
    </span>
  );
}
